import { useCallback, useMemo } from 'react';

import dotize from 'dotize';
import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import set from 'lodash-es/set';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { userContextVariables } from '@/modules/tryIt/components/VariablesSection/constants';
import { customInputValueRender } from '@/modules/tryIt/components/VariablesSection/helper';
import { Context } from '@/shared/types/chat';
import { removeEmptyObjects } from '@/util/util';

import { updateContext } from '../redux/actions';
import { selectContext } from '../redux/selectors';

export type ContextVariable = { label: string; value: string };

export type RawContextVariable = { label: string; value: unknown };

const UNSAFE_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);

const isPresent = (value: unknown): boolean => value !== null && value !== undefined;

export const isSafeVariableLabel = (label: string): boolean =>
  label.split(/[.[\]]+/).every((segment) => !UNSAFE_SEGMENTS.has(segment));

const flatVariables = (context: Context | null | undefined): [string, unknown][] => {
  if (!context) {
    return [];
  }
  const flat = dotize.convert(omit(context, ['global', 'tags'])) as Record<string, unknown> | null;
  return Object.entries(flat ?? {}).filter(([label]) => isSafeVariableLabel(label));
};

export const flattenRawContext = (context: Context | null | undefined): RawContextVariable[] =>
  flatVariables(context)
    .filter(([, value]) => isPresent(value))
    .map(([label, value]) => ({ label, value }));

const clearedLabels = (context: Context | null | undefined): string[] =>
  flatVariables(context)
    .filter(([, value]) => value === null)
    .map(([label]) => label);

const clearTarget = (label: string): string => {
  const bracket = label.indexOf('[');
  return bracket > 0 ? label.slice(0, bracket) : label;
};

const ELEMENT_LABEL = /^(.+)\[(\d+)\]$/;

const asArrayElement = (label: string): { path: string; index: number } | null => {
  const match = ELEMENT_LABEL.exec(label);
  return match ? { path: match[1], index: Number(match[2]) } : null;
};

/**
 * Flattens a context object into display rows — dot-notation keys, with `global`
 * and `tags` excluded (surfaced by the bundle/tags sections) and null-valued
 * variables dropped (a null marks a "removed" variable, still carried to the
 * brain to clear it). Shared by the test-mode editor and the read-only replay
 * view so both render the same variables.
 */
export const flattenContextVariables = (context: Context | null | undefined): ContextVariable[] => {
  if (!context) {
    return [];
  }
  const flat = dotize.convert(omit(context, ['global', 'tags'])) as Record<string, unknown>;
  return Object.keys(flat)
    .filter((label) => isPresent(flat[label]))
    .map((label) => ({ label, value: customInputValueRender(flat[label]) }));
};

const PRESET_LABELS = new Set(userContextVariables.map((variable) => variable.label));

/**
 * Translates the predefined `user.*` variable names to their localized labels;
 * custom names are returned verbatim. Shared by the test-mode editor and the
 * read-only replay Context view so both show the same friendly names.
 */
export const useVariableLabel = () => {
  const { t } = useTranslation();
  return useCallback(
    (label: string) =>
      PRESET_LABELS.has(label) ? t(`auto_complete.variable_labels.${label}`) : label,
    [t]
  );
};

/**
 * Reads the current (last) context entry from the `tryIt` slice, flattens it
 * into editable `{ label, value }` rows (dot-notation keys, mirroring the legacy
 * `useVariablesSection`), and exposes writers that persist edits back through
 * `updateContext`. `global` and `tags` are excluded — they're surfaced by the
 * bundle/tags sections, not as plain variables.
 */
export const useContextVariables = () => {
  const dispatch = useDispatch();
  const storeContext = useSelector(selectContext);

  const current = useMemo<Context>(
    () => storeContext[storeContext.length - 1] ?? {},
    [storeContext]
  );

  const variables = useMemo<ContextVariable[]>(() => flattenContextVariables(current), [current]);

  // Edits operate on the last (current) context entry and replace it in place,
  // so the next test send carries them. `slice(0, -1)` drops the stale current;
  // when the stack is empty this yields `[next]`, seeding the first entry.
  const writeCurrent = useCallback(
    (next: Context) => {
      const rest = cloneDeep(storeContext).slice(0, -1);
      dispatch(updateContext([...rest, next]));
    },
    [dispatch, storeContext]
  );

  const setVariables = useCallback(
    (entries: ContextVariable[]) => {
      const valid = entries.filter((entry) => entry.label && isSafeVariableLabel(entry.label));
      if (valid.length === 0) {
        return;
      }
      const next = cloneDeep(current);
      valid.forEach(({ label, value }) => set(next, label, value));
      writeCurrent(next);
    },
    [current, writeCurrent]
  );

  const setVariable = useCallback(
    (label: string, value: string) => setVariables([{ label, value }]),
    [setVariables]
  );

  // Nulls (rather than deletes) so the brain still receives the key and clears it.
  const removeVariables = useCallback(
    (labels: string[]) => {
      if (labels.length === 0) {
        return;
      }
      const next = cloneDeep(current);
      const droppedElements = new Map<string, Set<number>>();
      labels.forEach((label) => {
        const element = asArrayElement(label);
        if (!element) {
          set(next, label, null);
          return;
        }
        const indexes = droppedElements.get(element.path) ?? new Set<number>();
        indexes.add(element.index);
        droppedElements.set(element.path, indexes);
      });
      droppedElements.forEach((indexes, path) => {
        const value: unknown = get(next, path);
        if (!Array.isArray(value)) {
          return;
        }
        const kept = value.filter((entry, index) => !indexes.has(index));
        set(next, path, kept.length > 0 ? kept : null);
      });
      writeCurrent(removeEmptyObjects(next) as Context);
    },
    [current, writeCurrent]
  );

  const removeVariable = useCallback(
    (label: string) => removeVariables([label]),
    [removeVariables]
  );

  const replaceContext = useCallback(
    (incoming: Context, spare: string[] = []) => {
      const spared = new Set(spare);
      const existing = flattenRawContext(current);
      const arriving = new Set(flattenRawContext(incoming).map((variable) => variable.label));
      const next: Context = {
        ...cloneDeep(omit(incoming, ['global', 'tags'])),
        ...cloneDeep(pick(current, ['global', 'tags'])),
      };
      clearedLabels(current)
        .filter((label) => get(incoming, label) === undefined)
        .forEach((label) => set(next, label, null));
      existing
        .filter((variable) => !spared.has(variable.label) && !arriving.has(variable.label))
        .forEach((variable) => {
          const target = clearTarget(variable.label);
          if (get(incoming, target) === undefined) {
            set(next, target, null);
          }
        });
      existing
        .filter((variable) => spared.has(variable.label))
        .forEach(({ label, value }) => set(next, label, value));
      const cleaned = removeEmptyObjects(next) as Context;
      if (isEqual(cleaned, current)) {
        return;
      }
      writeCurrent(cleaned);
    },
    [current, writeCurrent]
  );

  return {
    context: current,
    variables,
    setVariable,
    setVariables,
    removeVariable,
    removeVariables,
    replaceContext,
  };
};
